import { LibraryIcon as PolygonTreasuryIcon } from '@heroicons/react/outline';
import { Card } from 'components/Card';
import { Section } from 'components/Section';
import { usePrice } from 'contexts';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCount, formatPercent, formatUsd, sumArray } from 'utils/number';
import { getPolygonTreasuryBalance } from 'utils/price';
import { Currency, CurrencyBalance } from 'utils/types';

const PolygonTreasury = () => {
  const [[balance, balanceMatic], setBalances] = useState([{}, {}] as [
    CurrencyBalance,
    CurrencyBalance
  ]);
  const { maticTokenPrices } = usePrice();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBalances = async () => {
      const balances = await getPolygonTreasuryBalance();
      setBalances(balances);
    };

    fetchBalances();
  }, []);

  const maticUsd = maticTokenPrices[Currency.MATIC]?.[Currency.USD] || 0;

  const currencies = (Object.keys(balanceMatic) as Array<Currency>)
    .filter((currency) => balance[currency])
    .sort((a, b) => balanceMatic[b]! - balanceMatic[a]!);
  const balanceTotalMatic = sumArray(Object.values(balanceMatic));
  const balanceTotalUsd = sumArray(Object.values(balanceMatic).map((matic) => matic * maticUsd));

  return (
    <div className="container space-y-10">
      <Section
        className="space-y-4"
        title={t('polygon_treasury.title')}
        icon={PolygonTreasuryIcon}
        actionTo="https://polygonscan.com/address/0xde4c22d69afe3c49ca3acad20bb4777eb5aebd75#tokentxns"
        actionTitle={t('polygon_treasury.explorer')}
        styleSlug="polygon"
      >
        <Card
          className="pb-4"
          styleSlug="polygon"
          body={
            <table className="w-full table-fixed text-lg text-left">
              <thead className={`uppercase text-polygon-primary dark:text-polygon-light`}>
                <tr>
                  <th className="pb-2 font-normal">{t('portfolio.wallet_breakdown.token')}</th>
                  <th className="pb-2 font-normal text-right">
                    {t('portfolio.wallet_breakdown.balance')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  className={`bg-polygon-lighter dark:bg-polygon-dark text-polygon-dark dark:text-white font-bold border-b border-gray-e6e6e6 dark:border-gray-444`}
                >
                  <td className="py-4 pl-2 uppercase text-gray-444 dark:text-white">
                    <img
                      src="/img/tokens/treasury/wmatic.png"
                      alt={t('polygon_treasury.title')}
                      className={`inline mr-4 h-12 w-12 rounded-full border-2 border-polygon-primary`}
                    />
                    <span>{t('polygon_treasury.total')}</span>
                  </td>
                  <td className="py-4 pr-2 text-right">{formatUsd(balanceTotalUsd)}</td>
                </tr>
                {currencies.map((currency, i) => (
                  <tr
                    key={currency}
                    className={
                      i !== currencies.length - 1
                        ? 'border-b border-gray-e6e6e6 dark:border-gray-444'
                        : ''
                    }
                  >
                    <td className="p-2 flex items-center">
                      <img
                        src={`/img/tokens/treasury/${currency.toLowerCase()}.png`}
                        alt={currency}
                        className={`mr-4 h-12 w-12 rounded-full border-2 border-polygon-primary ${
                          currency === Currency.PCOMB ? 'p-1' : ''
                        }`}
                      />

                      <div>
                        <span className="block text-gray-444 dark:text-white">{currency}</span>
                        <span className="block text-gray-999">
                          {formatCount(balance[currency]!)}
                        </span>
                      </div>
                    </td>

                    <td className="p-2 text-right">
                      {formatUsd(balanceMatic[currency]! * maticUsd)}{' '}
                      <span className="text-gray-999">
                        ({formatPercent(balanceMatic[currency]! / balanceTotalMatic)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        />
      </Section>
    </div>
  );
};

export default PolygonTreasury;
